import {
  INavigatorMessageAttributes,
  INavigatorSupportMessageAttributes,
  INavigatorSystemMessageAttributes,
  INavigatorUserMessageAttributes,
  NavigatorBaseMessage,
  NavigatorFile,
  NavigatorMessage,
  TwilioConversationMessage,
} from "@cruisen/contract"

/**
 * @param {Partial<INavigatorMessageAttributes>} attrs
 * @return {boolean}
 */
export function isNavigatorSystemMessageAttrs(
  attrs: Partial<INavigatorMessageAttributes>,
): attrs is INavigatorSystemMessageAttributes {
  return (
    attrs &&
    typeof attrs === "object" &&
    attrs.source === "system" &&
    attrs.type !== undefined &&
    attrs.tripId !== undefined
  )
}

/**
 * Transforms a Twilio message into a standardized Navigator message format.
 *
 * @param {TwilioConversationMessage} message - The Twilio message object to be transformed.
 * @return {NavigatorMessage} The transformed Navigator message object.
 */
export const transformTwilioMessageIntoMessage = (
  message: TwilioConversationMessage,
): NavigatorMessage => {
  const attrs = message.attributes as
    | INavigatorSystemMessageAttributes
    | INavigatorUserMessageAttributes
    | INavigatorSupportMessageAttributes

  const commonParams: Omit<NavigatorBaseMessage, "source"> = {
    id: message.sid,
    message: message.body || "",
    index: message.index,
    timestamp: new Date(message.dateCreated || "").getTime() || new Date().getTime(),
  }

  if (isNavigatorSystemMessageAttrs(attrs)) {
    return {
      ...attrs,
      ...commonParams,
    }
  }

  if (attrs.source === "support") {
    return {
      userId: message.author || "",
      userFirstName: attrs.firstName as string,
      userLastName: attrs.lastName as string,
      userAvatar: attrs.avatar || "",
      ...attrs,
      ...commonParams,
    }
  }

  if (message.author === "@navigator") {
    return {
      ...commonParams,
      source: "bot",
      type: attrs.type,
    }
  }

  return {
    ...commonParams,
    source: "user",
    userId: attrs.userId || message.author || "",
    userFirstName: attrs.firstName as string,
    userLastName: attrs.lastName as string,
    userAvatar: attrs?.avatar || "",
    type: attrs.type,
    file: (attrs.file as NavigatorFile) || null,
  }
}
